import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core";
import { FormBuilder, FormControl, FormGroup } from "@angular/forms";
import { ValueLabel } from "../value-label";
import { MatCheckboxChange } from "@angular/material/checkbox";
import { Observable, Subscription } from "rxjs";
import { map, startWith } from "rxjs/operators";

@Component({
  selector: "gem-filter-content",
  template: `
    <div class="filterDropDownMenu" [formGroup]="form">
      <mat-form-field appearance="outline" class="mt-2 w-full">
        <mat-label>{{ searchLabel }}</mat-label>
        <input matInput [formControl]="searchControl" />
        <mat-icon matSuffix>search</mat-icon>
      </mat-form-field>

      <ul formArrayName="selected">
        <li *ngFor="let option of filteredOptions$ | async; let i = index">
          <mat-checkbox [formControlName]="i" (change)="onCheckboxChange($event, option)">
            {{ option.label | translate }}
          </mat-checkbox>
        </li>
      </ul>

      <div class="actions">
        <button mat-stroked-button color="primary" (click)="onClose()">
          {{ "CLOSE" | translate }}
        </button>
      </div>
    </div>
  `,
  styleUrls: ["gem-filter-content.component.scss"],
  standalone: false,
})
export class GemFilterContentComponent implements OnInit, OnChanges, OnDestroy {
  @Input() options: ValueLabel[] = [];
  @Input() value: ValueLabel[] = [];
  @Input() searchLabel: string = "Search";
  @Input() reset: { search: boolean } = { search: false };

  @Output() selectionChange = new EventEmitter<{ checked: boolean; vl: ValueLabel }>();
  @Output() close = new EventEmitter<void>();

  form: FormGroup;
  searchControl: FormControl;
  filteredOptions$: Observable<ValueLabel[]>;
  subscriptions: Subscription[] = [];

  constructor(private fb: FormBuilder) {
    this.form = this.fb.group({
      selected: this.fb.array([]),
    });
    this.searchControl = this.fb.control("");
    this.filteredOptions$ = this.searchControl.valueChanges.pipe(
      startWith(""),
      map((val) => (typeof val === "string" ? val : val?.label)),
      map((label) => (label ? this.filter(label) : this.options.slice())),
    );
  }

  ngOnInit(): void {
    this.initFormArray();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // update form when options or value changes asynchronously
    if (changes["options"] || changes["value"]) {
      this.initFormArray();
    }

    if (changes["reset"] && this.reset?.search) {
      this.searchControl.setValue("");
    }
  }

  private initFormArray(): void {
    if (!this.options) return;
    const selectedArray = this.options.map((opt) => this.value?.some((v) => v.value === opt.value));
    this.form.setControl("selected", this.fb.array(selectedArray));
  }

  onCheckboxChange(event: MatCheckboxChange, option: ValueLabel): void {
    this.selectionChange.emit({ checked: event.checked, vl: option });
  }

  onClose(): void {
    this.close.emit();
  }

  private filter(label: string): ValueLabel[] {
    const filterValue = label.toLocaleLowerCase();
    return this.options.filter((option) => option.label.toLowerCase().includes(filterValue));
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
